import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import {
  Box,
  Text,
  HStack,
  VStack,
  IconButton,
  Spinner,
  useToast,
  useColorModeValue,
  Center,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DataDeltaProfits = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalDelta, setTotalDelta] = useState(0);
  const toast = useToast();

  const apiUrl = "https://blindfoldcapital.com/api/delta_profits/";

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Network response was not ok");
      const result = await response.json();
      setData(result.data);

      const total = result.data.reduce(
        (sum, entry) => sum + parseFloat(entry.delta_profit),
        0
      );
      setTotalDelta(total);

      toast({
        title: "Data refreshed.",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (err) {
      setError(err.message);
      toast({
        title: "Failed to fetch data",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const labels = data.map((entry) => formatDate(entry.timestamp));
  const executionFeesData = data.map((entry) =>
    parseFloat(entry.delta_profit)
  );

  const startDate = data.length > 0 ? formatDate(data[0].timestamp) : null;
  const endDate =
    data.length > 0 ? formatDate(data[data.length - 1].timestamp) : null;

  const chartData = {
    labels,
    datasets: [
      {
        label: "Net Earnings (USD)",
        data: executionFeesData,
        borderColor: "green",
        backgroundColor: "rgba(0, 255, 0, 0.1)",
        fill: true,
        tension: 0.2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          font: { size: 11 },
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        type: "category",
        ticks: {
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 20,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Net Earnings (USD)",
          font: { size: 12 },
        },
        ticks: {
          font: { size: 10 },
        },
      },
    },
  };

  const bg = useColorModeValue("white", "gray.800");

  return (
    <Box
      p={1}
      //borderRadius="lg"
      //boxShadow="lg"
      backgroundColor={bg}
      maxW="100%"
      w="full"
    >
      <VStack align="start" spacing={1}>
        <HStack justify="space-between" w="full">
          <Text fontFamily='Sentinel' fontWeight="bold">
            Total Net Profit: ${totalDelta.toFixed(2)}
          </Text>
          <IconButton
            aria-label="Refresh"
            icon={<RepeatIcon />}
            onClick={fetchData}
            size="sm"
            colorScheme="blue"
            isLoading={loading}
          />
        </HStack>

        {startDate && endDate && (
          <Text fontSize="sm" color="gray.500">
            Range: {startDate} – {endDate}
          </Text>
        )}

        {loading ? (
          <Center w="full" h="150px">
            <Spinner size="sm" />
          </Center>
        ) : error ? (
          <Text color="red.500">Error: {error}</Text>
        ) : (
          <Box w="full" h={{ base: "180px", md: "220px" }}>
            <Line data={chartData} options={chartOptions} />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default DataDeltaProfits;
